"use client"

import { useState, useEffect, useRef } from "react"
import styled from "styled-components"
import { useNavigate } from "react-router-dom"
import PostCard from "./PostCard"
import FeaturedSpaces from "../components/FeaturedSpaces"
import PostCreator from "../components/PostCreator"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const WelcomeCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  padding: 20px;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
  
  @media (max-width: 350px) {
    padding: 10px;
  }
`

const WelcomeTitle = styled.h1`
  font-size: 24px;
  margin-bottom: 10px;
  color: ${props => props.theme.textPrimary};
`

const WelcomeText = styled.p`
  color: ${props => props.theme.textSecondary};
  line-height: 1.5;
  margin: 0;
  font-size: 14px;
  opacity: 0.7;
`

const UserWelcomeSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
`

const UserProfileStories = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`

const StoryCircle = styled.div`
  position: relative;
  cursor: pointer;
`

const StoryAvatar = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${props => props.$hasStory ? 
    'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' : 
    'linear-gradient(45deg, #e5e7eb, #d1d5db)'};
  padding: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.05);
  }
`

const StoryInnerCircle = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: ${props => props.theme.cardBackground};
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StoryImage = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-image: ${props => props.$avatar ? `url(${props.$avatar})` : 'none'};
  background-size: cover;
  background-position: center;
  background-color: ${props => props.theme.borderLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textMuted};
`

const StoryLabel = styled.div`
  text-align: center;
  margin-top: 8px;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  max-width: 70px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const WelcomeContent = styled.div`
  flex: 1;
`

const AddStoryButton = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: ${props => props.theme.borderLight};
  border: 2px dashed ${props => props.theme.border};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: ${props => props.theme.textMuted};
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    border-color: ${props => props.theme.textMuted};
  }
`

const PostsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.textSecondary};
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
`

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.error};
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.error};
  border-radius: 8px;
`

export default function HomePage() {
  const { isLoggedIn, user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // 포스트 목록 가져오기 함수 (페이지네이션)
  const fetchPosts = async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      
      const response = await fetch(`/post/list?page=${pageNum}&limit=10`, {
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (append) {
          setPosts(prev => [...prev, ...data.posts]);
        } else {
          setPosts(data.posts);
        }
        setHasMore(data.has_more);
        setPage(pageNum);
      } else {
        console.error("포스트 목록 로드 실패:", data.message);
        setError(data.message || "포스트를 불러오는데 실패했습니다.");
      }
    } catch (error) {
      console.error("포스트 목록 로드 오류:", error);
      setError("서버 연결에 문제가 발생했습니다.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // 무한스크롤 - IntersectionObserver
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        fetchPosts(page + 1, true);
      }
    }, { threshold: 0.1 });

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, page]);

  // 컴포넌트 마운트 시 포스트 목록 가져오기
  useEffect(() => {
    fetchPosts(1);
  }, []);

  // 새 포스트 생성 후 목록 업데이트
  const handlePostCreated = (newPost) => {
    console.log("새 게시물 생성됨:", newPost);
    // 첫 페이지부터 다시 로드
    setPage(1);
    setHasMore(true);
    fetchPosts(1);
  };

  // 로그인 필요 시 호출되는 함수
  const handleLoginRequired = () => {
    navigate('/');
    // 모바일 로그인 패널 열기 (MainLayout에서 처리)
    // 전역 상태나 이벤트를 통해 MainLayout의 toggleMobileLogin 호출
    const loginEvent = new CustomEvent('showLoginModal');
    window.dispatchEvent(loginEvent);
  };

  return (
    <Container>
      <PostCreator 
        user={user} 
        isLoggedIn={isLoggedIn} 
        onPostCreated={handlePostCreated}
        onLoginRequired={handleLoginRequired}
      />
      
      {isLoggedIn && (
        <WelcomeCard theme={theme}>
          <UserProfileStories>
            <StoryCircle>
              <StoryAvatar $hasStory={true}>
                <StoryInnerCircle theme={theme}>
                  <StoryImage theme={theme} $avatar={user?.avatar_url}>
                    {!user?.avatar_url && <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>}
                  </StoryImage>
                </StoryInnerCircle>
              </StoryAvatar>
              <StoryLabel theme={theme}>내 스토리</StoryLabel>
            </StoryCircle>
            
            <StoryCircle>
              <AddStoryButton theme={theme} onClick={() => navigate('/people')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </AddStoryButton>
              <StoryLabel theme={theme}>추가</StoryLabel>
            </StoryCircle>
          </UserProfileStories>
        </WelcomeCard>
      )}

      <FeaturedSpaces />

      <PostsContainer>
        {loading ? (
          <LoadingIndicator>포스트를 불러오는 중...</LoadingIndicator>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : posts.length === 0 ? (
          <LoadingIndicator>작성된 포스트가 없습니다.</LoadingIndicator>
        ) : (
          <>
          {posts.map(post => {
            // 이미지 처리 - JSON 문자열이거나 배열일 수 있음
            let processedImages = [];
            if (post.images) {
              let images = post.images;
              // JSON 문자열인 경우 파싱
              if (typeof images === 'string') {
                try {
                  images = JSON.parse(images);
                } catch (e) {
                  console.error('이미지 파싱 오류:', e);
                  images = [];
                }
              }
              // 배열인 경우 URL 추출
              if (Array.isArray(images)) {
                processedImages = images.map(img => 
                  typeof img === 'object' ? (img.url || img.image_url) : img
                ).filter(url => url);
              }
            }
            
            return (
              <PostCard 
                key={post.id} 
                post={{
                  id: post.id,
                  author: post.author_username,
                  authorAvatar: post.author_avatar,
                  timestamp: post.created_at,
                  images: processedImages,
                  imageCaption: post.images && post.images.length > 0 && typeof post.images[0] === 'object' ? post.images[0].caption : null,
                  tags: post.tags,
                  description: post.content,
                  like_count: post.like_count,
                  comment_count: post.comment_count
                }}
                onPostDeleted={() => fetchPosts(1)}
                onPostUpdated={() => fetchPosts(1)}
              />
            );
          })}
          <div ref={loadMoreRef} style={{ height: '20px', margin: '20px 0' }}>
            {loadingMore && <LoadingIndicator>더 불러오는 중...</LoadingIndicator>}
            {!hasMore && posts.length > 0 && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px' }}>
                모든 포스트를 불러왔습니다 ✨
              </div>
            )}
          </div>
          </>
        )}
      </PostsContainer>
    </Container>
  );
}